import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { Combobox } from '@headlessui/react';
import { LeagueService, TeamService, SeasonService, UserService } from '../../services/firebaseService';
import { getUserTeamsForLeagueSeason } from '../../utils/teamUtils';

export default function ClaimTeam() {
  const { leagueId, seasonId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [userClaimedTeams, setUserClaimedTeams] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successCount, setSuccessCount] = useState(0);

  // Set page title
  useEffect(() => {
    document.title = 'Claim a team';
    return () => {
      document.title = 'Survivor Fantasy League';
    };
  }, []);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (leagueId && seasonId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, seasonId, currentUser, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load league data
      const leagueData = await LeagueService.getLeague(leagueId);
      if (!leagueData) {
        throw new Error('League not found');
      }
      setLeague(leagueData);

      // Load season data
      const seasonData = await SeasonService.getSeason(seasonId);
      if (!seasonData) {
        throw new Error('Season not found');
      }
      setSeason(seasonData);

      // Load teams for this league-season
      const teamsData = await TeamService.getTeamsForLeagueSeason(leagueId, seasonId);
      const sortedTeams = teamsData.sort((a, b) => a.name.localeCompare(b.name));
      setTeams(sortedTeams);

      // Load user's claimed teams for this league-season
      if (userProfile && userProfile.teams) {
        const claimed = getUserTeamsForLeagueSeason(userProfile.teams, leagueId, seasonId);
        setUserClaimedTeams(claimed);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.message || 'Failed to load data. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter out already claimed teams from the dropdown
  const availableTeams = teams.filter(team => !userClaimedTeams.includes(team.id));

  // Headless UI Combobox (single mode) fires onChange(null) when the search
  // input is cleared — ignore invalid IDs so they never enter selection state.
  const validSelectedTeamIds = useMemo(
    () =>
      selectedTeamIds.filter(
        (teamId) => teamId && teams.some((team) => team.id === teamId)
      ),
    [selectedTeamIds, teams]
  );

  // Filter teams based on search query
  const filteredTeams = useMemo(() => {
    if (!query) {
      return availableTeams;
    }
    const lowerQuery = query.toLowerCase();
    return availableTeams.filter(team =>
      team.name.toLowerCase().includes(lowerQuery)
    );
  }, [query, availableTeams]);

  const handleToggleTeam = (teamId) => {
    // Combobox clears selection with null when the search input is emptied.
    if (!teamId) {
      return;
    }

    setSelectedTeamIds(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
    setQuery(''); // Clear search when selecting
  };

  const handleRemoveTeam = (teamId) => {
    setSelectedTeamIds(prev => prev.filter(id => id !== teamId));
  };

  const handleClaimTeams = async (e) => {
    e.preventDefault();
    
    if (validSelectedTeamIds.length === 0) {
      setError('Please select at least one team to claim.');
      return;
    }

    // Filter out teams that are already claimed
    const teamsToClaim = validSelectedTeamIds.filter(
      (teamId) => !userClaimedTeams.includes(teamId)
    );
    
    if (teamsToClaim.length === 0) {
      setError('All selected teams have already been claimed.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess(false);

      // Claim all selected teams in a single atomic operation
      // This prevents race conditions when claiming multiple teams
      await UserService.claimTeams(currentUser.uid, teamsToClaim, leagueId, seasonId);
      
      setSuccess(true);
      setSuccessCount(teamsToClaim.length);
      
      // Update local state
      setUserClaimedTeams([...userClaimedTeams, ...teamsToClaim]);
      
      // Refresh userProfile to get updated teams data and roles
      // Call updateUserProfile with empty object to trigger profile refresh in AuthContext
      try {
        await updateUserProfile({});
      } catch (refreshError) {
        console.warn('Could not refresh user profile:', refreshError);
        // Continue anyway - the teams are claimed, user can refresh page manually
      }
      
      // Clear selected teams
      setSelectedTeamIds([]);
      setQuery('');
      
      // Redirect to homepage after a delay to allow profile refresh to complete
      // The homepage will show the updated teams and roles
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error claiming teams:', error);
      setError(error.message || 'Failed to claim teams. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading teams...</p>
        </div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <div className="mt-4 text-center">
            <h3 className="text-lg font-medium text-gray-900">Error</h3>
            <p className="mt-2 text-sm text-gray-500">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-survivor-100 rounded-full mb-4">
            <UserGroupIcon className="w-8 h-8 text-survivor-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Claim Your Team
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            {league?.name} - {season?.name}
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  {successCount === 1 ? 'Team Claimed Successfully!' : `${successCount} Teams Claimed Successfully!`}
                </h3>
                <p className="mt-1 text-sm text-green-700">
                  You can now submit predictions for {successCount === 1 ? 'this team' : 'these teams'}.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error
                </h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Already Claimed Teams */}
        {userClaimedTeams.length > 0 && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">
              Your Claimed Teams
            </h3>
            <ul className="space-y-1">
              {userClaimedTeams.map(teamId => {
                const team = teams.find(t => t.id === teamId);
                return team ? (
                  <li key={teamId} className="text-sm text-blue-700">
                    ✓ {team.name}
                  </li>
                ) : null;
              })}
            </ul>
          </div>
        )}

        {/* Claim Team Form */}
        <div className="bg-white shadow-lg rounded-lg p-6">
          {availableTeams.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">
                {userClaimedTeams.length > 0 
                  ? 'You have claimed all available teams for this league-season.'
                  : 'No teams available to claim.'}
              </p>
              <button
                onClick={() => navigate(-1)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Go Back
              </button>
            </div>
          ) : (
            <form onSubmit={handleClaimTeams} className="space-y-6">
              {/* Selected Teams Display */}
              {validSelectedTeamIds.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selected Teams ({validSelectedTeamIds.length}):
                  </label>
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-md bg-gray-50 min-h-[60px]">
                    {validSelectedTeamIds.map(teamId => {
                      const team = teams.find(t => t.id === teamId);
                      return team ? (
                        <span
                          key={teamId}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-survivor-100 text-survivor-800 border border-survivor-200"
                        >
                          {team.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveTeam(teamId)}
                            className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full text-survivor-600 hover:bg-survivor-200 hover:text-survivor-900 focus:outline-none focus:ring-2 focus:ring-survivor-500"
                            aria-label={`Remove ${team.name}`}
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Team Selection Combobox */}
              <div>
                <label htmlFor="team-search" className="block text-sm font-medium text-gray-700 mb-2">
                  {validSelectedTeamIds.length > 0 ? 'Add more teams:' : 'Search and select teams to claim:'}
                </label>
                <Combobox
                  value={null}
                  onChange={handleToggleTeam}
                  disabled={submitting}
                >
                  <div className="relative">
                    <div className="relative">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <Combobox.Input
                        id="team-search"
                        className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 focus:outline-none focus:ring-2 focus:ring-survivor-500 focus:border-survivor-500 disabled:bg-gray-50 disabled:text-gray-500"
                        placeholder="Type to search teams..."
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {filteredTeams.length === 0 && query !== '' ? (
                        <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                          No teams found matching "{query}".
                        </div>
                      ) : filteredTeams.length === 0 ? (
                        <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                          No teams available to claim.
                        </div>
                      ) : (
                        filteredTeams.map((team) => {
                          const isSelected = validSelectedTeamIds.includes(team.id);
                          return (
                            <Combobox.Option
                              key={team.id}
                              value={team.id}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-survivor-600 text-white' : 'text-gray-900'
                                }`
                              }
                            >
                              {({ active }) => (
                                <>
                                  <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                    {team.name}
                                  </span>
                                  {isSelected && (
                                    <span
                                      className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-survivor-600'
                                      }`}
                                    >
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          );
                        })
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
                <p className="mt-2 text-xs text-gray-500">
                  You can claim multiple teams. Each team can be claimed by multiple players. Click a team to select it, or type to search.
                </p>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={submitting || validSelectedTeamIds.length === 0}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting 
                    ? 'Claiming...' 
                    : validSelectedTeamIds.length === 1
                    ? 'Claim Team'
                    : `Claim ${validSelectedTeamIds.length} Teams`
                  }
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}

